import { Button } from "antd";
import { observer } from "mobx-react-lite";
import * as React from "react";
import {
  SpecificationsFilter,
  SpecificationType,
  ZSpecification,
} from "src/business/specification/ZSpecification";
import { columnDisplayId } from "src/business/sysData/sysColumns";
import { makeSidLabelsFromKeys, optionsMaker } from "src/common/optionsMaker";
import { BlankLink } from "src/components/BlankLink";
import { MdmDrawer } from "src/components/MdmDrawer";
import { MdmTable } from "src/components/MdmTable";
import { RtSelection } from "src/components/ReactiveTable/components/RtSelection";
import { RtColumn } from "src/components/ReactiveTable/RtColumn";
import { RtStore } from "src/components/ReactiveTable/RtStore";
import { ToolBox2 } from "src/components/ToolBox2";
import { makeUrl } from "src/routes/makeUrl";
import { PageUrl } from "src/routes/PageUrl";
import { ZSidLabel } from "src/types";

type PropsSpecificationSelectModal = {
  specType: SpecificationType;
  tableStore?: RtStore<ZSpecification, SpecificationsFilter>;
  srcSelected: ZSidLabel[];
  close(): void;
  onApply(values: ZSidLabel[]): void;
};

export const SpecificationSelectModal: React.FC<PropsSpecificationSelectModal> =
  observer((props) => {
    const { specType, tableStore, srcSelected, close, onApply } = props;

    const columns = React.useMemo(
      () => makeSpecificationColumns(specType),
      [specType],
    );

    const makeOption = React.useMemo(
      () => optionsMaker<ZSpecification>(srcSelected, ({ name }) => name),
      [srcSelected],
    );

    if (!tableStore) return null;

    const handleApply = () => {
      const values = makeSidLabelsFromKeys(tableStore.selectedKeys, makeOption);

      onApply(values);
      close();
    };

    return (
      <MdmDrawer
        title="Выбор спецификации"
        open
        onClose={close}
        wide
        extra={
          <Button
            type="primary"
            disabled={tableStore.selectedKeys.length === 0}
            onClick={handleApply}
          >
            Выбрать
          </Button>
        }
      >
        <ToolBox2
          mainTool={<RtSelection store={tableStore} makeOption={makeOption} />}
        >
          <MdmTable
            store={tableStore}
            columns={columns}
            resizableColumns
            rowClick={{
              type: "click",
              handler: (row) => {
                tableStore.changeSelection(
                  row.id,
                  tableStore.selectionType === "radio" ? true : "toggle",
                );
              },
            }}
          />
        </ToolBox2>
      </MdmDrawer>
    );
  });

const makeSpecificationColumns = (
  specType: SpecificationType,
): RtColumn<ZSpecification>[] => {
  const cardPage = specificationCardPages[specType];

  return [
    columnDisplayId,
    {
      key: "name",
      title: "Системное название",
      width: "25%",
      minWidth: 180,
      render: ({ id, name }) => (
        <BlankLink href={makeUrl(cardPage, { id })}>{name}</BlankLink>
      ),
    },
    {
      key: "displayName",
      title: "Название",
      width: "25%",
      minWidth: 180,
      render: ({ displayName }) => displayName,
    },
    {
      key: "description",
      title: "Описание",
      minWidth: 240,
      render: ({ description }) => description,
    },
  ];
};

const specificationCardPages: Record<SpecificationType, PageUrl> = {
  primary: PageUrl.managementPrimSpecCard,
  secondary: PageUrl.managementSecondSpecCard,
  values: PageUrl.managementValueSpecCard,
};
